import { toastError, toastSuccess } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApp } from '@/core/state/appStore'
import { useDokumentBlob } from '@/core/hooks/useDokumentBlob'
import { AUFGABE, type AufgabeEntry } from '../../aufgabe'
import { getSpalteStand } from '../../api/spalteStand'
import {
  nameAbweichung,
  uploadGeva,
  uploadKlett,
  type GevaKlettErrorBody,
  type GevaUploadResult,
  type KlettUploadResult,
  type NameAbweichung,
  type NamePruefung,
} from '../../api/gevaKlett'
import {
  gevaKlettTabs,
  useStatusFehlerToast,
  type GevaKlettStatusState,
  type GevaKlettStep,
} from './useGevaKlettStatus'
import { classifyGevaKlettFailure } from './errors'
import { GevaKlettFehlerPrompt, NameAbweichendPrompt, fehlerZeileText } from './prompts'
import { GevaHochladen, type GevaAnsicht } from './GevaHochladen'

const AUFGABEN: readonly AufgabeEntry[] = Object.values(AUFGABE)

type Upload<T> = { antwort: Promise<T>; file: File }

const inflight = {
  klett: new Map<string, Upload<KlettUploadResult>>(),
  geva: new Map<string, Upload<GevaUploadResult>>(),
}
const followers = new WeakMap<Promise<unknown>, object>()

function follow(upload: Promise<unknown>): () => boolean {
  const token = {}
  followers.set(upload, token)
  return () => followers.get(upload) === token
}

function track<T>(map: Map<string, Upload<T>>, id: string, upload: Upload<T>): Upload<T> {
  map.set(id, upload)
  const forget = () => {
    if (map.get(id) === upload) map.delete(id)
  }
  upload.antwort.then(forget, forget)
  return upload
}

type NameWarnung = { step: GevaKlettStep; file: File; abweichung: NameAbweichung }

export function GevaKlett({ coachingId, status }: { coachingId: string | null; status: GevaKlettStatusState }) {
  const { state, dispatch, reloadDashboard } = useApp()
  const { t } = useTranslation('vergabeStaff')
  const { status: stand, loading: statusLoading, statusError, statusFehler, markDone } = status
  useStatusFehlerToast(statusFehler)

  const [klettLoading, setKlettLoading] = useState(false)
  const [klettResult, setKlettResult] = useState<KlettUploadResult | null>(null)
  const [klettAnsicht, setKlettAnsicht] = useState<GevaAnsicht>('dokument')
  const [gevaLoading, setGevaLoading] = useState(false)
  const [gevaResult, setGevaResult] = useState<GevaUploadResult | null>(null)
  const [gevaAnsicht, setGevaAnsicht] = useState<GevaAnsicht>('dokument')
  const [fehlerQueue, setFehlerQueue] = useState<GevaKlettErrorBody[]>([])
  const [nameWarnung, setNameWarnung] = useState<NameWarnung | null>(null)

  const mountedRef = useRef(true)
  const savedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (savedRef.current) reloadDashboard()
    }
    // `reloadDashboard` only dispatches; the cleanup is once per box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openCoachingRef = useRef(state.screen?.coachingId)
  openCoachingRef.current = state.screen?.coachingId
  const stillOpen = (id: string) => mountedRef.current && openCoachingRef.current === id

  const activeStep: GevaKlettStep = state.screen?.tab === 'geva' ? 'geva' : 'klett'

  const pdf = useDokumentBlob(statusLoading || statusError ? null : stand[activeStep].dokument_id)

  function showFailure(id: string, step: GevaKlettStep, file: File, e: unknown) {
    const abweichung = nameAbweichung(e)
    if (abweichung && stillOpen(id)) {
      setNameWarnung({ step, file, abweichung })
      return
    }
    console.error(step === 'klett' ? 'Klett-Upload fehlgeschlagen:' : 'GEVA-Upload fehlgeschlagen:', e)
    const failure = classifyGevaKlettFailure(e, t)
    if (failure.kind === 'prompt') {
      if (mountedRef.current) setFehlerQueue((prev) => [...prev, failure.body])
      else toastError([failure.body.meldung, ...failure.body.fehler.map((f) => fehlerZeileText(f, t))].join(' '))
    } else {
      toastError(failure.message)
    }
    if (failure.reload) reloadDashboard()
  }


  async function moveOn(id: string) {
    if (!mountedRef.current) {
      reloadDashboard()
      return
    }
    const naechste = await getSpalteStand(id).catch((e: unknown) => {
      console.error('Spaltenstand laden fehlgeschlagen:', e)
      if (stillOpen(id)) toastError(t('gevaKlett.naechsterFehler'))
      return null
    })
    const landing = AUFGABEN.find((e) => e.dbName === naechste?.aufgabe)?.landing
    if (landing && stillOpen(id)) dispatch({ type: 'CHANGE_PHASE', target: { ...landing } })
  }

  useEffect(() => {
    if (!coachingId) return
    const klett = inflight.klett.get(coachingId)
    if (klett) void followKlett(coachingId, klett)
    const geva = inflight.geva.get(coachingId)
    if (geva) void followGeva(coachingId, geva)
    // Once per box open, for the coaching it opened for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingId])

  async function followUpload<T>(
    id: string,
    step: GevaKlettStep,
    upload: Upload<T>,
    setLoading: (loading: boolean) => void,
    accept: (result: T) => void,
  ) {
    const owns = follow(upload.antwort)
    setLoading(true)
    try {
      const result = await upload.antwort
      if (!owns()) return
      savedRef.current = true
      toastSuccess(t(step === 'klett' ? 'gevaKlett.klett.success' : 'gevaKlett.geva.success'))
      accept(result)
      if (!mountedRef.current) void moveOn(id)
    } catch (e) {
      if (owns()) showFailure(id, step, upload.file, e)
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }

  const followKlett = (id: string, upload: Upload<KlettUploadResult>) =>
    followUpload(id, 'klett', upload, setKlettLoading, (result) => {
      markDone('klett', { ...result, fertig: true })
      if (mountedRef.current) {
        setKlettResult(result)
        setKlettAnsicht('dokument')
      }
    })
  const followGeva = (id: string, upload: Upload<GevaUploadResult>) =>
    followUpload(id, 'geva', upload, setGevaLoading, (result) => {
      markDone('geva', { ...result, fertig: true })
      if (mountedRef.current) {
        setGevaResult(result)
        setGevaAnsicht('dokument')
      }
    })

  function runKlett(file: File, name: NamePruefung = 'pruefen') {
    if (!coachingId || inflight.klett.has(coachingId)) return
    void followKlett(coachingId, track(inflight.klett, coachingId, { antwort: uploadKlett(coachingId, file, name), file }))
  }

  function runGeva(file: File, name: NamePruefung = 'pruefen') {
    if (!coachingId || inflight.geva.has(coachingId)) return
    void followGeva(coachingId, track(inflight.geva, coachingId, { antwort: uploadGeva(coachingId, file, name), file }))
  }

  function weiter() {
    if (!coachingId || !(activeStep === 'klett' ? klettResult : gevaResult)) return
    if (activeStep === 'klett') setKlettResult(null)
    else setGevaResult(null)
    void moveOn(coachingId)
  }

  function trotzdemSpeichern({ step, file }: NameWarnung) {
    setNameWarnung(null)
    if (step === 'klett') runKlett(file, 'bestaetigt')
    else runGeva(file, 'bestaetigt')
  }

  return (
    <>
      <GevaHochladen
        {...(activeStep === 'geva'
          ? { step: 'geva' as const, stand: stand.geva, result: gevaResult }
          : { step: 'klett' as const, stand: stand.klett, result: klettResult })}
        tabs={gevaKlettTabs(stand)}
        pdf={pdf}
        statusLoading={statusLoading}
        statusError={statusError}
        loading={activeStep === 'geva' ? gevaLoading : klettLoading}
        ansicht={activeStep === 'geva' ? gevaAnsicht : klettAnsicht}
        onAnsicht={activeStep === 'geva' ? setGevaAnsicht : setKlettAnsicht}
        onFile={activeStep === 'geva' ? runGeva : runKlett}
        onWeiter={weiter}
      />
      {fehlerQueue[0] && (
        <GevaKlettFehlerPrompt
          meldung={fehlerQueue[0].meldung}
          fehler={fehlerQueue[0].fehler}
          onClose={() => setFehlerQueue((prev) => prev.slice(1))}
        />
      )}
      {nameWarnung && (
        <NameAbweichendPrompt
          abweichung={nameWarnung.abweichung}
          onSpeichern={() => trotzdemSpeichern(nameWarnung)}
          onAbbrechen={() => setNameWarnung(null)}
        />
      )}
    </>
  )
}
